import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Landmark, CreditCard, PhoneCall } from 'lucide-react';
import Modal from '../Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyId } from '../../hooks/useCompanyId';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { findAccountIdByEmail } from '../../services/firestoreService';

/**
 * Upsell + purchase modal shown on the portal department pages when a client
 * has used all of their Implementation Hours for the current billing period.
 *
 * Mirrors the "Add More Hours" purchase flow on the Client Dashboard: it
 * redirects to a Stripe Checkout session created by the
 * `createStrategyCallCheckoutSession` callable. Once the purchase completes,
 * the webhook adds the call(s) to the account's allotment and the client can
 * schedule their strategy call.
 */
const StrategyCallUpsellModal = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const location = useLocation();

  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset to a clean state each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handlePurchase = async (paymentMethod) => {
    if (!currentUser?.email || quantity < 1 || !paymentMethod) return;
    setError(null);
    setLoading(true);
    try {
      const checkoutCompanyId =
        companyId || (await findAccountIdByEmail(currentUser.email, 'Clients'));
      const createSession = httpsCallable(functions, 'createStrategyCallCheckoutSession');
      const isPortal = location.pathname.startsWith('/portal');
      const returnPath = isPortal
        ? '/portal/checkout/strategy-calls/return'
        : '/start/checkout/strategy-calls/return';

      const { data } = await createSession({
        quantity,
        paymentMethod,
        customerEmail: currentUser.email,
        returnPath,
        origin: window.location.origin,
        ...(checkoutCompanyId && { companyId: checkoutCompanyId }),
      });

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError('No checkout URL received');
        setLoading(false);
      }
    } catch (err) {
      console.error('createStrategyCallCheckoutSession error:', err);
      setError(err.message || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <div className="w-full max-w-md">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <PhoneCall className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold dark:text-white">You're out of Implementation Hours</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            You've used all of your Implementation Hours for this period. Add more hours below to
            book your next session right away.
          </p>
        </div>

        <div className="mb-4 flex flex-col items-center">
          <label className="mb-2 block text-sm font-medium dark:text-gray-300">Quantity</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              −
            </button>
            <span className="w-8 text-center text-lg font-semibold dark:text-white">{quantity}</span>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(10, q + 1))}
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              +
            </button>
          </div>
        </div>

        <div className="mb-4 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Total</span>
            <span className="font-semibold dark:text-white">
              ${(300 * quantity).toLocaleString()}
            </span>
          </div>
        </div>

        {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex gap-3">
          <button
            onClick={() => handlePurchase('ach')}
            disabled={loading}
            className="flex flex-1 flex-col items-center gap-2 rounded-lg border-2 border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700"
          >
            <Landmark className="h-6 w-6" />
            <span>Pay with Bank Account (ACH)</span>
          </button>
          <button
            onClick={() => handlePurchase('card')}
            disabled={loading}
            className="flex flex-1 flex-col items-center gap-2 rounded-lg border-2 border-red-500 bg-red-600 px-4 py-3 font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CreditCard className="h-6 w-6" />
            <span>Pay with Credit Card (3% Fee)</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StrategyCallUpsellModal;
